import { Badge } from '@artic/ui/badge';
import { Skeleton } from '@artic/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { usePartnerId } from '../../features/inbox/use-partner-name';
import { partnerContainersQueryOptions } from '../../features/products/queries';
import { projectBreadcrumbBadges } from '../../features/project-planning/project-route-chrome';
import { ProjectStepper } from '../../features/project-planning/project-stepper';
import { projectPlanningDetailQueryOptions } from '../../features/project-planning/queries';

const ProjectGantt = lazy(() =>
  import('../../features/project-planning/gantt/project-gantt').then((mod) => ({
    default: mod.ProjectGantt,
  })),
);

export const Route = createFileRoute('/projects/$projectId_/gantt')({
  staticData: {
    breadcrumb: (match, queryClient) => {
      const { projectId } = match.params as { projectId: string };
      const data = queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey);
      return data ? `${data.project_name} — Gantt` : 'Project Gantt';
    },
    breadcrumbBadges: projectBreadcrumbBadges,
    layout: 'full-height',
  },
  component: ProjectGanttContent,
});

function ProjectGanttContent() {
  const { projectId } = Route.useParams();
  const partnerId = usePartnerId();

  const {
    data: project,
    isLoading: projectLoading,
    isError: projectError,
  } = useQuery(projectPlanningDetailQueryOptions(projectId));

  const { isLoading: containersLoading, isError: containersError } = useQuery(
    partnerContainersQueryOptions(partnerId),
  );

  if (projectLoading || containersLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (projectError || containersError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Error Loading Project</h2>
        <p className="text-muted-foreground text-sm">
          Could not load the project or container configuration. Please try again.
        </p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <Badge variant="outline">Project not found</Badge>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <ProjectStepper
        projectId={project.project_id}
        active="work_orders"
        projectStatus={project.project_status}
        isProjectRedrafting={project.project_status === 'redrafting'}
      />
      <div className="min-h-0 flex-1">
        <Suspense
          fallback={
            <div className="flex flex-col gap-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-[60vh] w-full" />
            </div>
          }
        >
          <ProjectGantt projectId={projectId} partnerId={partnerId} />
        </Suspense>
      </div>
    </div>
  );
}
